// Modules
import { Children, isValidElement, useEffect, useState } from 'react';
import cn from 'classnames';

// Types
import type { JSX } from 'react';
import type { TTabsProps } from './Tabs.type';

// Styles
import styles from './styles/styles.module.scss';

// Components
import TabList from './TabList';
import TabPanelList from './TabPanelList';

// Helpers
import { isFunction } from '../../helpers/typeChecker';

// Constants
import {
	TAB_LIST_DISPLAY_NAME,
	TAB_PANEL_LIST_DISPLAY_NAME,
	TABS_CHILDREN_DISPLAY_NAME,
	TABS_DEFAULT_PROPS
} from './Tab.constant';

const Tabs = (props: TTabsProps) => {
	const {
		activeTab = TABS_DEFAULT_PROPS.activeTab,
		children,
		className,
		onChange,
		position = TABS_DEFAULT_PROPS.position,
		...restProps
	} = props;

	const tabsCN = cn(styles.legion_tabs, className, styles[position!]);

	const [activeTabState, setActiveTabState] = useState(activeTab);

	useEffect(() => {
		setActiveTabState(activeTab);
	}, [activeTab]);

	const handleChangeTab = (tabIndex?: number) => {
		if (isFunction(onChange)) {
			onChange(tabIndex);
		} else {
			setActiveTabState(tabIndex);
		}
	}

	return (
		<div className={tabsCN} {...restProps}>
			{
				// Filter out the children that are not `TabList` or `TabPanelList`
				Children.toArray(children).filter(child => TABS_CHILDREN_DISPLAY_NAME.includes((child as JSX.Element)?.type?.displayName)).map((child, childIndex) => {
					if (isValidElement(child) && (child as JSX.Element)?.type?.displayName === TAB_LIST_DISPLAY_NAME) {
						const { className: tabListClassName, key, ...tabListProps } = (child as JSX.Element).props;
						const tabListCN = cn(styles.tab_list_section, tabListClassName);
						const tabListKey = key || `tab-list-${childIndex}`;

						return (
							<TabList
								key={tabListKey}
								{...tabListProps}
								className={tabListCN}
								activeTab={activeTabState}
								onChange={handleChangeTab}
							/>
						)
					} else if (isValidElement(child) && (child as JSX.Element)?.type?.displayName === TAB_PANEL_LIST_DISPLAY_NAME) {
						const { className: panelListClassName, key, ...panelListProps } = (child as JSX.Element).props
						const panelListCN = cn(styles.panel_list_section, panelListClassName);
						const panelListKey = key || `panel-list-${childIndex}`;

						return (
							<TabPanelList
								key={panelListKey}
								{...panelListProps}
								className={panelListCN}
								activePanel={activeTabState}
							/>
						)
					}
				})
			}
		</div>
	);
}

export default Tabs;
